import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { authStorage } from '@/lib/auth-storage';
import { authService } from '@/services/auth';
import { ApiError } from '@/lib/api-client';
import type { AuthUser } from '@just-frotas/shared';

// Estado de auth global. LoginPage chama login() e usa setUser/setToken.
// AuthProvider escuta evento auth:logout-forced disparado pelo api-client
// quando refresh falha - zera estado pra que ProtectedRoute redirecione.

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  setSession: (user: AuthUser, accessToken: string) => void;
  clearSession: () => void;
  refreshMe: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Normaliza payload de user vindo da API. Backend deveria sempre devolver
// roles/permissions como arrays, mas se algum cenario raro retornar null
// ou undefined, evitamos quebrar consumidores que fazem .includes(...).
function normalizeUser(u: AuthUser | null | undefined): AuthUser | null {
  if (!u) return null;
  return {
    ...u,
    roles: Array.isArray(u.roles) ? u.roles : [],
    permissions: Array.isArray(u.permissions) ? u.permissions : [],
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUserRaw] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const setUser = useCallback((u: AuthUser | null) => setUserRaw(normalizeUser(u)), []);

  // Boot: se ha token persistido, valida via /auth/me. Se 401, refresh
  // automatico do api-client tenta recuperar; se falhar, evento
  // auth:logout-forced limpa estado.
  useEffect(() => {
    mountedRef.current = true;
    const token = authStorage.getToken();
    if (!token) {
      setLoading(false);
      return;
    }
    authService
      .me()
      .then((u) => {
        if (mountedRef.current) setUser(u);
      })
      .catch((err) => {
        if (err instanceof ApiError && err.status === 401) {
          authStorage.clearToken();
        }
      })
      .finally(() => {
        if (mountedRef.current) setLoading(false);
      });
    return () => {
      mountedRef.current = false;
    };
  }, [setUser]);

  // Refresh falhou no api-client => sessao terminou. Limpa estado React.
  useEffect(() => {
    function onForcedLogout() {
      setUser(null);
    }
    window.addEventListener('auth:logout-forced', onForcedLogout);
    return () => window.removeEventListener('auth:logout-forced', onForcedLogout);
  }, [setUser]);

  const setSession = useCallback(
    (nextUser: AuthUser, accessToken: string) => {
      authStorage.setToken(accessToken);
      setUser(nextUser);
    },
    [setUser],
  );

  const clearSession = useCallback(() => {
    authStorage.clearToken();
    // Limpa perfil ativo persistido. Reconciliacao do ProfileContext recalcula
    // default ao logar com user diferente, mas remover aqui evita lixo entre
    // sessoes e garante que login subsequente nao herde estado de outro user.
    try {
      localStorage.removeItem('just_active_profile');
    } catch {
      /* localStorage bloqueado: noop */
    }
    setUser(null);
  }, [setUser]);

  const refreshMe = useCallback(async () => {
    const u = await authService.me();
    setUser(u);
  }, [setUser]);

  const logout = useCallback(async () => {
    try {
      await authService.logout();
    } catch {
      /* mesmo que /logout falhe, limpamos local */
    }
    clearSession();
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(
    () => ({ user, loading, setSession, clearSession, refreshMe, logout }),
    [user, loading, setSession, clearSession, refreshMe, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth deve ser usado dentro de <AuthProvider>');
  return ctx;
}
